import React, { useEffect, useState } from 'react';
import { Calendar, Phone, ArrowDown } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section id="accueil" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#050505]" />
        
        {/* Animated gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00D9FF]/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FF1493]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#5B7FFF]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }} />
      </div>

      {/* Gradient line at top */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00D9FF] via-[#5B7FFF] to-[#FF1493] to-transparent" />

      {/* Content */}
      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Logo */}
          <div className={`mb-8 flex justify-center transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
          }`}>
            <img 
              src={businessInfo.logo} 
              alt="DNA Barbershop" 
              className="h-48 md:h-64 w-auto drop-shadow-2xl"
            />
          </div>

          {/* Title */}
          <h1 
            className={`text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ fontFamily: 'Montserrat, sans-serif', textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}
          >
            <span className="block">{t.hero.title1}</span>
            <span className="block mt-3 gradient-text">
              {t.hero.title2}
            </span>
          </h1>

          {/* Subtitle */}
          <p className={`text-xl md:text-2xl text-gray-100 mb-12 max-w-3xl mx-auto leading-relaxed transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}
          >
            {t.hero.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row gap-5 justify-center items-center mb-16 transition-all duration-1000 delay-900 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <a href={businessInfo.bookingUrl} target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                className="group relative bg-gradient-to-r from-[#00D9FF] via-[#5B7FFF] to-[#FF1493] text-white font-bold px-12 py-7 text-xl shadow-2xl shadow-[#00D9FF]/40 transition-all duration-500 hover:scale-110 hover:shadow-[#FF1493]/60 border-0 overflow-hidden rounded-xl"
              >
                <span className="relative z-10 flex items-center">
                  <Calendar className="w-6 h-6 mr-3 group-hover:animate-bounce" />
                  {t.hero.bookNow}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </Button>
            </a>
            <a href="tel:4389221776">
              <Button
                size="lg"
                className="group relative bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white/20 font-bold px-12 py-7 text-xl transition-all duration-500 hover:scale-110 rounded-xl"
              >
                <span className="relative z-10 flex items-center">
                  <Phone className="w-6 h-6 mr-3 group-hover:animate-pulse" />
                  (438) 922-1776
                </span>
              </Button>
            </a>
          </div>

          {/* Quick Info */}
          <div className={`flex flex-col sm:flex-row gap-6 justify-center items-center text-white text-base transition-all duration-1000 delay-1100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="flex items-center gap-2 px-5 py-3 rounded-full backdrop-blur-md bg-white/10 border border-white/20">
              <span>📍 {t.hero.address}</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 rounded-full backdrop-blur-md bg-white/10 border border-white/20">
              <span>🕐 {t.hero.hours}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce cursor-pointer" onClick={() => document.getElementById('videos')?.scrollIntoView({ behavior: 'smooth' })}>
        <div className="w-8 h-12 border-2 border-white/60 rounded-full flex justify-center p-2 backdrop-blur-sm bg-black/20">
          <div className="w-2 h-3 bg-gradient-to-b from-[#00D9FF] to-[#FF1493] rounded-full animate-pulse" />
        </div>
        <ArrowDown className="w-6 h-6 text-white/80 mx-auto mt-2 animate-pulse" />
      </div>

      {/* Bottom gradient transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />
    </section>
  );
};
